import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, ShieldCheck } from "lucide-react";
import { LOGO_URL, BRAND, TAGLINE } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function AuthPage() {
  const navigate = useNavigate();
  const { user, login, register, apiErr } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [agree, setAgree] = useState(false);

  useEffect(() => {
    if (user) navigate(user.role === "consumer" ? "/portal" : "/app", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !agree) { toast.error("Please accept the Terms & Conditions to continue"); return; }
    setLoading(true);
    try {
      const u = mode === "login" ? await login(form.email, form.password) : await register(form.name, form.email, form.password);
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      navigate(u?.role === "consumer" ? "/portal" : "/app");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-[#F6EEEE] border-r border-neutral-200 overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-40" />
        <div className="relative">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-neutral-600 hover:text-[#C1121F] transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </button>
        </div>
        <div className="relative">
          <div className="bg-white rounded-2xl p-6 inline-block shadow-2xl shadow-red-900/10">
            <img src={LOGO_URL} alt={BRAND} className="w-60 max-w-full h-auto object-contain" />
          </div>
          <h2 className="mt-8 font-display text-3xl font-extrabold max-w-sm leading-tight">
            The identity-theft dispute command center.
          </h2>
          <p className="mt-3 text-neutral-500 max-w-sm">
            CRM, credit-report matching, statutory letter studio and certified-mail tracking — unified.
          </p>
        </div>
        <div className="relative text-[11px] font-mono uppercase tracking-[0.2em] text-[#C1121F]">{TAGLINE}</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <span className="bg-white rounded-lg p-1.5"><img src={LOGO_URL} alt={BRAND} className="h-9 w-auto object-contain" /></span>
            <span className="font-display font-bold">{BRAND}</span>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#C1121F]/40 bg-[#C1121F]/10 px-3 py-1 text-[11px] font-mono uppercase tracking-wider text-[#9B0E18]">
            <ShieldCheck className="h-3.5 w-3.5" /> Secure Access
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold">
            {mode === "login" ? "Sign in" : "Create your account"}
          </h1>
          <p className="mt-2 text-neutral-500">
            {mode === "login" ? "Access your dispute workspace." : "Start managing identity-theft disputes."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <Label htmlFor="name" className="text-neutral-600">Full name</Label>
                <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 bg-[#FFFFFF] border-neutral-200 h-11" placeholder="Jane Advocate" data-testid="auth-name-input" />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-neutral-600">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 bg-[#FFFFFF] border-neutral-200 h-11" placeholder="you@example.com" data-testid="auth-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-neutral-600">Password</Label>
              <Input id="password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 bg-[#FFFFFF] border-neutral-200 h-11" placeholder="••••••••" data-testid="auth-password-input" />
            </div>
            <Button type="submit" disabled={loading || (mode === "register" && !agree)}
              className="w-full h-11 rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold"
              data-testid="auth-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? "Sign In" : "Create Account"}
            </Button>
            {mode === "register" && (
              <label className="flex items-start gap-2 text-xs text-neutral-500 cursor-pointer" data-testid="terms-agree-label">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 accent-[#C1121F]" data-testid="terms-agree-checkbox" />
                <span>I agree to the <a href="/terms" target="_blank" rel="noreferrer" className="text-[#C1121F] hover:text-[#9B0E18]">Terms &amp; Conditions</a>, understand results are not guaranteed, and will upload and keep current my credit reports, government ID, Social Security card, and proof of address.</span>
              </label>
            )}
          </form>

          <p className="mt-6 text-sm text-neutral-500">
            {mode === "login" ? "New here?" : "Already have an account?"}{" "}
            <button
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-[#C1121F] hover:text-[#9B0E18] font-medium"
              data-testid="auth-mode-toggle-button">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
